import {PureComponent} from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import {
  Card,
  CardActions,
  CardContent,
  Tooltip,
  Chip,
  Toolbar,
  Typography,
  MenuItem,
  MenuList,
  Popover,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import {MuiThemeProvider, createMuiTheme} from '@material-ui/core/styles';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  CheckBox as CheckBoxIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Create as CreateIcon,
  Delete as DeleteIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  ViewColumn as ViewColumnIcon,
  LocalDrink as LocalDrinkIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import {Container, Row, Col} from 'react-grid-system';
import {
  common,
  amber,
  green,
  grey,
  lightBlue,
  orange,
  purple,
  red,
  yellow,
} from '@material-ui/core/colors';
/* eslint-disable-next-line  import/no-cycle */
import Media from 'SbsSuite/Presentation/Media';
import StepListContainer from 'SbsSuite/Container/StepListContainer';

const timestampToString = (timestamp) =>
  timestamp
    ? new Date(timestamp).toLocaleDateString('en-AU', {
        weekday: 'short',
        year: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'unknown';
const createStepTheme = (colour) => ({
  palette: {
    primary: colour,
    secondary: {main: common.black},
  },
  overrides: {
    MuiToolbar: {
      root: {
        backgroundColor: colour['400'],
      },
    },
    MuiCard: {
      root: {
        borderLeft: `8px solid ${colour['400']}`,
      },
    },
    MuiCardActions: {
      root: {
        backgroundColor: colour['400'],
      },
    },
  },
});
const greenTheme = createMuiTheme(createStepTheme(green));
const lightBlueTheme = createMuiTheme(createStepTheme(lightBlue));
const orangeTheme = createMuiTheme(createStepTheme(orange));
const yellowTheme = createMuiTheme(createStepTheme(yellow));
const redTheme = createMuiTheme({palette: {primary: red}});
const purpleTheme = createMuiTheme({palette: {primary: purple}});

class Step extends PureComponent {
  anchorEl = null;

  constructor(props) {
    super(props);
    this.state = {
      popoverOpen: false,
    };
  }

  handleTouchTapPopover = () => this.setState({popoverOpen: true});

  handleClosePopover = () => this.setState({popoverOpen: false});

  render() {
    const {
      props: {
        location,
        index,
        step,
        isSubSbsLoaded,
        onEditStep,
        onDeleteStep,
        onAddStepAfter,
        onLoadSubSbs,
        onAddToSchedule,
        onMoveStepUp,
        onMoveStepDown,
        onClickDecision,
        isDuplicationSelected,
        onDuplicationToggleSelectedStep,
        onOpenStepConsumableCreateDialog,
        isLoadAll,
      },
      state: {popoverOpen},
    } = this;
    let theme = {};

    switch (step.stepType.type) {
      case 'Work Instruction':
        theme = lightBlueTheme;
        break;
      case 'Process Outcome':
        theme = greenTheme;
        break;
      case 'Sub SBS':
        theme = orangeTheme;
        break;
      case 'Decision':
        theme = yellowTheme;
        break;
      default:
        break;
    }

    const isProcessOutcome = step.stepType.type === 'Process Outcome';
    const isDecision = step.stepType.type === 'Decision';

    if (isDecision && !step.outgoingLinks) {
      step.outgoingLinks = [];
    }

    const isSubSbs = !!step.subSbs && step.stepType.type === 'Sub SBS';
    const stepConsumableUnitConst =
      step.stepConsumableUnits != null &&
      step.stepConsumableUnits.map(
        ({
          id,
          consumable: {name: consumable, productCode, supplier},
          quantity,
          metricUnit: {name: metricUnit},
        }) => (
          <ul key={id}>
            <li>
              {`${quantity} ${metricUnit} of ${consumable} (${supplier}: ${productCode})`}
            </li>
          </ul>
        ),
      );
    let subSbsStatusColour = 'inherit';

    if (isSubSbs) {
      switch (step.subSbs.sbsStatus.status) {
        case 'Completed':
          subSbsStatusColour = green['500'];
          break;
        case 'WIP':
          subSbsStatusColour = amber['500'];
          break;
        case 'Kaizen':
          subSbsStatusColour = grey['500'];
          break;
        case 'Outdated':
          subSbsStatusColour = red['500'];
          break;
        default:
          break;
      }
    }

    return (
      <MuiThemeProvider theme={theme}>
        <Card style={{margin: 8}} className="printMarginTopZero">
          <Toolbar>
            <div
              style={{
                flex: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Typography variant="h6" style={{marginRight: 24}}>
                {index + 1}
              </Typography>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  marginRight: 8,
                }}
              >
                {isSubSbs ? (
                  <Typography variant="h6" style={{opacity: 0.54}}>
                    {step.subSbs.title}
                  </Typography>
                ) : (
                  <Typography variant="h6" style={{opacity: 0.54}}>
                    {step.stepType.type}
                  </Typography>
                )}
                <Typography variant="caption">
                  {`edited ${timestampToString(step.modifiedDt)} by ${
                    step.modifiedBy.fullName
                  }`}
                </Typography>
              </div>
              {isSubSbs && (
                <Chip
                  label={step.subSbs.sbsStatus.status}
                  style={{
                    backgroundColor: subSbsStatusColour,
                    marginRight: 8,
                  }}
                />
              )}
              {step.hh + step.mm + step.ss > 0 ||
              isSubSbs ||
              isProcessOutcome ||
              isDecision ? (
                <Typography
                  variant="h6"
                  style={{marginRight: 8, opacity: 0.54}}
                >
                  {`${step.hh}h ${step.mm}m ${step.ss}s`}
                </Typography>
              ) : (
                <Chip
                  label="Time?"
                  style={{backgroundColor: red['500'], marginRight: 8}}
                />
              )}
              <Typography variant="h6" style={{opacity: 0.54}}>
                {step.stepLevel.level}
              </Typography>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
              }}
              className="printDisplayNone"
            >
              {isSubSbs && onLoadSubSbs && (
                <Tooltip
                  title={`${!isSubSbsLoaded ? 'Load' : 'Unload'} Sub SBS`}
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onLoadSubSbs(step)}
                      style={{marginLeft: 8, opacity: 0.54}}
                      startIcon={
                        !isSubSbsLoaded ? (
                          <ExpandMoreIcon />
                        ) : (
                          <ExpandLessIcon />
                        )
                      }
                    >
                      {!isSubSbsLoaded ? 'load' : 'unload'}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {!onDuplicationToggleSelectedStep && (
                <>
                  <Tooltip
                    title={
                      !onEditStep
                        ? 'You do not have write permission for this SBS'
                        : 'Already Editing Another Step'
                        ? 'Already Editing Another Step'
                        : 'Edit Step'
                    }
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => onEditStep(step)}
                        style={{marginLeft: 8, opacity: 0.54}}
                        disabled={!onEditStep}
                        startIcon={<CreateIcon />}
                      >
                        edit: step
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Step Actions">
                    <Button
                      variant="outlined"
                      color="secondary"
                      buttonRef={(node) => {
                        this.anchorEl = node;
                      }}
                      onClick={this.handleTouchTapPopover}
                      style={{marginLeft: 8, opacity: 0.54}}
                      endIcon={<ArrowDropDownIcon />}
                    >
                      Step Actions
                    </Button>
                  </Tooltip>
                  <Popover
                    open={popoverOpen}
                    anchorEl={this.anchorEl}
                    anchorReference="anchorEl"
                    anchorOrigin={{
                      horizontal: 'right',
                      vertical: 'bottom',
                    }}
                    transformOrigin={{
                      horizontal: 'right',
                      vertical: 'top',
                    }}
                    onClose={this.handleClosePopover}
                  >
                    <MenuList component="nav">
                      <Tooltip
                        title={
                          onAddStepAfter
                            ? isDecision
                              ? 'Add Yes Step After'
                              : 'Add Step After'
                            : 'Finish Adding Step'
                        }
                      >
                        <span>
                          <MenuItem
                            onClick={() => {
                              this.handleClosePopover();
                              onAddStepAfter(index, 1);
                            }}
                            disabled={!onAddStepAfter}
                          >
                            <ListItemIcon>
                              <AddCircleOutlineIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                isDecision
                                  ? 'add: yes step after'
                                  : 'add: step after'
                              }
                            />
                          </MenuItem>
                        </span>
                      </Tooltip>
                      {isDecision && (
                        <Tooltip
                          title={
                            onAddStepAfter
                              ? 'Add No Step After'
                              : 'Finish Adding Step'
                          }
                        >
                          <span>
                            <MenuItem
                              onClick={() => {
                                this.handleClosePopover();
                                onAddStepAfter(index, 0);
                              }}
                              disabled={!onAddStepAfter}
                            >
                              <ListItemIcon>
                                <AddCircleOutlineIcon />
                              </ListItemIcon>
                              <ListItemText primary="add: no step after" />
                            </MenuItem>
                          </span>
                        </Tooltip>
                      )}
                      {/* add consumables button */}
                      <Tooltip
                        title={
                          !onOpenStepConsumableCreateDialog
                            ? 'You do not have write permission for this SBS'
                            : 'Add Consumable'
                        }
                      >
                        <span>
                          <MenuItem
                            onClick={() => {
                              this.handleClosePopover();
                              onOpenStepConsumableCreateDialog(step);
                            }}
                            disabled={!onOpenStepConsumableCreateDialog}
                          >
                            <ListItemIcon>
                              <LocalDrinkIcon />
                            </ListItemIcon>
                            <ListItemText primary="add: consumable" />
                          </MenuItem>
                        </span>
                      </Tooltip>
                      {/* add step to schedule button */}
                      <Tooltip title="Add Step to Schedule">
                        <MenuItem
                          onClick={() => {
                            this.handleClosePopover();
                            onAddToSchedule(step);
                          }}
                        >
                          <ListItemIcon>
                            <ScheduleIcon />
                          </ListItemIcon>
                          <ListItemText primary="add: to schedule" />
                        </MenuItem>
                      </Tooltip>
                      <Tooltip
                        title={
                          onMoveStepUp ? 'Move Step Up' : 'Cannot Move Step Up'
                        }
                      >
                        <span>
                          <MenuItem
                            onClick={() => {
                              this.handleClosePopover();
                              onMoveStepUp(index);
                            }}
                            disabled={!onMoveStepUp}
                          >
                            <ListItemIcon>
                              <ArrowUpwardIcon />
                            </ListItemIcon>
                            <ListItemText primary="move up" />
                          </MenuItem>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          onMoveStepDown
                            ? 'Move Step Down'
                            : 'Cannot Move Step Down'
                        }
                      >
                        <span>
                          <MenuItem
                            onClick={() => {
                              this.handleClosePopover();
                              onMoveStepDown(index + 1);
                            }}
                            disabled={!onMoveStepDown}
                          >
                            <ListItemIcon>
                              <ArrowDownwardIcon />
                            </ListItemIcon>
                            <ListItemText primary="move: down" />
                          </MenuItem>
                        </span>
                      </Tooltip>
                      <Tooltip
                        title={
                          !onDeleteStep
                            ? 'You do not have write permission for this SBS'
                            : 'Delete Step'
                        }
                      >
                        <span>
                          <MenuItem
                            onClick={() => {
                              this.handleClosePopover();
                              onDeleteStep(step);
                            }}
                            disabled={!onDeleteStep}
                          >
                            <ListItemIcon>
                              <DeleteIcon />
                            </ListItemIcon>
                            <ListItemText primary="delete: step" />
                          </MenuItem>
                        </span>
                      </Tooltip>
                      {isSubSbs && (
                        <Tooltip title="View Sub SBS">
                          <MenuItem
                            component="a"
                            href={`/#/Sbss/${step.subSbs.id}`}
                          >
                            <ListItemIcon>
                              <ViewColumnIcon />
                            </ListItemIcon>
                            <ListItemText primary="list: Sub SBS" />
                          </MenuItem>
                        </Tooltip>
                      )}
                    </MenuList>
                  </Popover>
                </>
              )}
              {onDuplicationToggleSelectedStep && (
                <MuiThemeProvider theme={purpleTheme}>
                  <Tooltip title="Duplicate">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => onDuplicationToggleSelectedStep(step.id)}
                      style={{marginLeft: 8}}
                      startIcon={
                        isDuplicationSelected ? (
                          <CheckBoxIcon />
                        ) : (
                          <CheckBoxOutlineBlankIcon />
                        )
                      }
                    >
                      duplicate
                    </Button>
                  </Tooltip>
                </MuiThemeProvider>
              )}
            </div>
          </Toolbar>
          <CardContent>
            <Container fluid>
              <Row>
                <Col xs={12} md={6}>
                  {step.instruction && step.instruction !== '<p><br /></p>' && (
                    <Card elevation={5} style={{margin: 8}}>
                      <CardContent>
                        <Typography
                          variant="body1"
                          dangerouslySetInnerHTML={{
                            __html: step.instruction.replace(
                              /<\/p><p>/g,
                              '<br>',
                            ),
                          }}
                        />
                      </CardContent>
                    </Card>
                  )}
                  {step.detail && step.detail !== '<p><br /></p>' && (
                    <Card elevation={1} style={{margin: 8}}>
                      <CardContent>
                        <Typography
                          variant="body1"
                          dangerouslySetInnerHTML={{
                            __html: step.detail.replace(/<\/p><p>/g, '<br>'),
                          }}
                        />
                      </CardContent>
                    </Card>
                  )}
                </Col>
                <Col
                  xs={12}
                  md={6}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  {isDecision &&
                    step.outgoingLinks.map((outgoingLink) => (
                      <MuiThemeProvider
                        theme={outgoingLink.decision ? greenTheme : redTheme}
                        key={outgoingLink.id}
                      >
                        <Tooltip
                          title={
                            outgoingLink.decision
                              ? 'Show Yes Step'
                              : 'Show No Step'
                          }
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            fullWidth
                            onClick={() =>
                              onClickDecision(index, outgoingLink.nextStep)
                            }
                            style={{margin: 4}}
                          >
                            {outgoingLink.decision
                              ? 'decision: yes'
                              : 'decision: no'}
                          </Button>
                        </Tooltip>
                      </MuiThemeProvider>
                    ))}
                  {(!isSubSbs || !isDecision || step.media) && (
                    <Media media={step.media} />
                  )}
                </Col>
              </Row>
            </Container>
            {step.badges.length > 0 && (
              <div style={{marginTop: 8}}>
                {step.badges.map((badge) => (
                  <img
                    key={badge.id}
                    style={{margin: '4px'}}
                    src={badge.src}
                    alt="badge"
                  />
                ))}
              </div>
            )}
            {/* load sub sbs */}
            {step.subSbs && isSubSbsLoaded && (
              <div style={{marginTop: 8}}>
                <StepListContainer
                  location={location}
                  sbs={step.subSbs}
                  isSubSbs
                  isLoadAll={isLoadAll}
                />
              </div>
            )}
            {<div>{stepConsumableUnitConst}</div>}
          </CardContent>
          {step.subSbs && isSubSbsLoaded && (
            <CardActions>
              {/* close button at the end of sub-sbs */}
              <Tooltip title="Close Sub-SBS">
                <Button
                  variant="contained"
                  onClick={() => onLoadSubSbs(step)}
                  style={{marginLeft: 8, marginBottom: 8, float: 'right'}}
                  startIcon={<ExpandLessIcon />}
                >
                  Unload SUB-SBS
                </Button>
              </Tooltip>
            </CardActions>
          )}
        </Card>
      </MuiThemeProvider>
    );
  }
}

Step.propTypes = {
  location: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  step: PropTypes.object.isRequired,
  isSubSbsLoaded: PropTypes.bool,
  onEditStep: PropTypes.func,
  onDeleteStep: PropTypes.func,
  onAddStepAfter: PropTypes.func,
  onLoadSubSbs: PropTypes.func,
  onAddToSchedule: PropTypes.func,
  onMoveStepUp: PropTypes.func,
  onMoveStepDown: PropTypes.func,
  onClickDecision: PropTypes.func,
  isDuplicationSelected: PropTypes.bool,
  onDuplicationToggleSelectedStep: PropTypes.func,
  onOpenStepConsumableCreateDialog: PropTypes.func,
  isLoadAll: PropTypes.bool.isRequired,
};

Step.defaultProps = {
  isSubSbsLoaded: false,
  onEditStep: undefined,
  onDeleteStep: undefined,
  onAddStepAfter: undefined,
  onLoadSubSbs: undefined,
  onMoveStepUp: undefined,
  onMoveStepDown: undefined,
  onClickDecision: undefined,
  isDuplicationSelected: false,
  onDuplicationToggleSelectedStep: undefined,
};

export default Step;
